/**
 * Fabric.js v7 patch: draw freshly loaded images from a decoded bitmap.
 *
 * A canvas decodes an <img> on the main thread when it draws it, again for
 * each size it is drawn at, and `img.decode()` does not help: that decode
 * serves the page, not canvases. Opening a template with 12 sizes decoded the
 * same few images over and over inside the frames that first draw them. An
 * ImageBitmap made from the image file is decoded once, off the main thread
 * (one made from the <img> itself decodes on the main thread), and draws
 * without any decode.
 *
 * Images loaded through `fabricImageLoader` get a bitmap here, shared by every
 * image with the same URL, and Fabric draws from it while the image shows its
 * own element. The bitmap is only kept while images keep arriving: a drawn
 * image sits in its own cache canvas and is redrawn rarely (zoom), and a full
 * decode of every image would otherwise stay in memory for good. A filtered
 * image, one whose element was swapped (video, GIF frames), or one whose
 * bitmap was released draws from its element as before.
 */
import { FabricImage } from 'fabric';

// long enough for every size of a template to make its first draw
const HOLD_MS = 30_000;

const bitmaps = new WeakMap<HTMLImageElement, ImageBitmap>();
const byUrl = new Map<
  string,
  { bitmap: Promise<ImageBitmap>; release?: ReturnType<typeof setTimeout> }
>();

/**
 * Decode a loaded image off the main thread and draw it from the result.
 * Rejects when no bitmap matching the image could be made; the image then
 * draws from its element as before.
 */
export async function drawFromBitmap(el: HTMLImageElement): Promise<void> {
  const url = el.currentSrc || el.src;
  let entry = byUrl.get(url);
  if (!entry) {
    entry = { bitmap: decodeFile(url) };
    byUrl.set(url, entry);
  }
  const held = entry;

  let bitmap: ImageBitmap;
  try {
    bitmap = await held.bitmap;
  } catch (error) {
    if (byUrl.get(url) === held) byUrl.delete(url);
    throw error;
  }
  if (bitmap.width !== el.naturalWidth || bitmap.height !== el.naturalHeight) {
    throw new Error('Decoded bitmap does not match the image size');
  }

  bitmaps.set(el, bitmap);
  clearTimeout(held.release);
  held.release = setTimeout(() => {
    byUrl.delete(url);
    bitmap.close();
  }, HOLD_MS);
}

const renderFill = FabricImage.prototype._renderFill;

FabricImage.prototype._renderFill = function (
  this: FabricImage,
  ctx: CanvasRenderingContext2D
) {
  const element = this._element;
  const bitmap =
    element === this._originalElement && element instanceof HTMLImageElement
      ? bitmaps.get(element)
      : undefined;
  // a released bitmap is closed, and a closed bitmap has no size
  if (!bitmap?.width) {
    renderFill.call(this, ctx);
    return;
  }

  // fabric only reads the size of what it draws, and the bitmap's is the image's
  this._element = bitmap as unknown as HTMLImageElement;
  try {
    renderFill.call(this, ctx);
  } finally {
    this._element = element;
  }
};

/*
 * Helpers
 */

// the <img> has just loaded this URL, so the bytes come from the HTTP cache
async function decodeFile(url: string): Promise<ImageBitmap> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Image request failed: ${response.status}`);
  return createImageBitmap(await response.blob(), {
    imageOrientation: 'from-image'
  });
}
